import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useSearchParams } from "react-router-dom";
import { logout as logoutApi } from "@/lib/api/authService";
import { logout } from "@/features/auth/authSlice";
import { useAppDispatch } from "@/app/hooks";
import { Loader2 } from "lucide-react";
import { PropertyConfirmations } from "../components/PropertyConfirmations";
import { PrimaryApplicant } from "../components/PrimaryApplicant";
import { CoApplicantSection } from "../components/CoApplicantSection";
import { InviteeSuccessView } from "../components/InviteeSuccessView";
import { EditInviteeDialog } from "../components/EditInviteeDialog";
import { InviteeLookupDialog } from "../components/InviteeLookupDialog";
import { BlockingConditionsModal } from "@/components/ui/BlockingConditionsModal";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import type { Invitee } from "../types/invitee";
import {
  inviteeFormSchema,
  coApplicantSchema,
  validateRentAmountForSubmit,
  parseRentAmountNumeric,
  RENT_AMOUNT_MIN_SUBMIT_MESSAGE,
  isInviteEmailFormatInvalid,
  INVITEE_EMAIL_INVALID_MESSAGE,
} from "../utils/inviteeValidators";
import type { InviteeFormSchemaType } from "../utils/inviteeValidators";
import { validateData } from "../utils/inviteeFormatters";
import {
  createInvitee,
  sendInvitation,
  lookupInvitee,
  addInviteLog,
} from "../services/inviteeService";
import type {
  CreateInviteeResponse,
  InviteeLookupResult,
} from "../types/invitee";
import { PageHeader } from "../components/PageHeader";
import {
  showSuccess,
  showError,
  showWarning,
  formatApiError,
} from "@/lib/utils/notifications";
import { trackApplicationEvent } from "@/lib/utils/analytics";
import styles from "../styles/inviteeAdd.module.css";
import { useUserRole } from "@/hooks/useUserRole";
import type { FieldErrors, FieldPath, UseFormSetError } from "react-hook-form";
import {
  LEASE_LEAD_TIME_VALIDATION_HTML,
  isLeadTimeValidationError,
} from "../constants/leadTimeValidation";
import { InviteeValidationMessageModal } from "../components/InviteeValidationMessageModal";

/**
 * Validation order for Send Invitation / Apply Now: same as the form layout so required fields
 * are corrected before rent (required → min > $10 modal), then co-applicants.
 */
const INVITEE_PRIMARY_FIELD_ORDER = [
  "crime",
  "approved_by_liberty",
  "allow_to_movein",
  "first_name",
  "last_name",
  "email",
  "mobile_no",
  "property_id",
  "unit_number",
  "rent_amount",
  "lease_start_date",
  "lease_duration",
  "income_verified",
  "income",
  "income_period",
] as const;

const INVITEE_CO_APPLICANT_FIELD_ORDER = [
  "first_name",
  "last_name",
  "email",
  "mobile_no",
  "relationship_type",
  "living_unit",
  "is_approved_require",
  "income_verified",
  "income",
] as const;

function getMessageAtPath(
  errors: FieldErrors | undefined,
  path: string,
): string | undefined {
  if (!errors || !path) return undefined;
  const parts = path.split(".").filter(Boolean);
  let cur: unknown = errors;
  for (const p of parts) {
    if (cur == null || typeof cur !== "object") return undefined;
    const key = /^\d+$/.test(p) ? Number(p) : p;
    cur = (cur as Record<string | number, unknown>)[key];
  }
  if (
    cur &&
    typeof cur === "object" &&
    "message" in cur &&
    typeof (cur as { message?: string }).message === "string"
  ) {
    return (cur as { message: string }).message;
  }
  return undefined;
}

function formatInviteeSubmitErrorMessage(
  message: string,
  fieldPath: string,
): string {
  const coApplicantMatch = fieldPath.match(/^co_applicant\.(\d+)\./);
  if (!coApplicantMatch) return message;

  return `Co-Applicant ${Number(coApplicantMatch[1]) + 1}: ${message}`;
}

/** First error in form order (required fields before rent_amount, then co-applicants). */
function getFirstInviteeSubmitError(
  errors: FieldErrors,
): { message: string; fieldPath: string } | null {
  for (const field of INVITEE_PRIMARY_FIELD_ORDER) {
    const msg = getMessageAtPath(errors, field);
    if (msg) return { message: msg, fieldPath: field };
  }
  const coArr = errors.co_applicant;
  if (Array.isArray(coArr)) {
    for (let i = 0; i < coArr.length; i++) {
      for (const field of INVITEE_CO_APPLICANT_FIELD_ORDER) {
        const path = `co_applicant.${i}.${field}`;
        const msg = getMessageAtPath(errors, path);
        if (msg) {
          return {
            message: formatInviteeSubmitErrorMessage(msg, path),
            fieldPath: path,
          };
        }
      }
    }
  }
  return null;
}

function isInvalidInviteEmail(value: unknown): boolean {
  return isInviteEmailFormatInvalid(value);
}

function getInvalidEmailSummary(values: InviteeFormSchemaType): string | null {
  const invalidEmailMessages: string[] = [];

  if (isInvalidInviteEmail(values.email)) {
    invalidEmailMessages.push(`Primary Applicant: ${INVITEE_EMAIL_INVALID_MESSAGE}`);
  }

  const coApplicants = values.co_applicant;
  if (Array.isArray(coApplicants)) {
    for (let i = 0; i < coApplicants.length; i++) {
      const email = coApplicants[i]?.email?.trim() ?? "";
      if (isInvalidInviteEmail(email)) {
        invalidEmailMessages.push(
          `Co-Applicant ${i + 1}: ${INVITEE_EMAIL_INVALID_MESSAGE}`,
        );
      }
    }
  }

  if (invalidEmailMessages.length === 0) return null;

  return invalidEmailMessages.join("\n");
}

const INVALID_INVITEE_EMAIL_FIELD_MESSAGE = INVITEE_EMAIL_INVALID_MESSAGE;

function setInvalidInviteEmailFieldErrors(
  values: InviteeFormSchemaType,
  setError: UseFormSetError<InviteeFormSchemaType>,
  message = INVALID_INVITEE_EMAIL_FIELD_MESSAGE,
): FieldErrors {
  const fieldErrors: FieldErrors = {};

  if (isInvalidInviteEmail(values.email)) {
    setError("email", { type: "manual", message });
    fieldErrors.email = { type: "manual", message };
  }

  const coApplicants = values.co_applicant;
  if (Array.isArray(coApplicants)) {
    const coApplicantErrors: Array<Record<string, { type: string; message: string }>> = [];

    coApplicants.forEach((coApplicant, index) => {
      if (isInvalidInviteEmail(coApplicant?.email)) {
        setError(`co_applicant.${index}.email` as FieldPath<InviteeFormSchemaType>, {
          type: "manual",
          message,
        });
        coApplicantErrors[index] = {
          email: { type: "manual", message },
        };
      }
    });

    if (coApplicantErrors.length > 0) {
      fieldErrors.co_applicant = coApplicantErrors as unknown as FieldErrors["co_applicant"];
    }
  }

  return fieldErrors;
}

function isEmailValidationMessage(message: string): boolean {
  return /\bemail\b/i.test(message) && /(valid|invalid)/i.test(message);
}

function normalizeInviteeEmailErrorPath(path: string): FieldPath<InviteeFormSchemaType> | null {
  const normalized = path
    .replace(/\[(\d+)\]/g, ".$1")
    .replace(/\[email\]/g, ".email");
  if (normalized === "email") return "email";

  const coApplicantMatch = normalized.match(/^co_applicant\.(\d+)\.email(?:\.|$)/);
  if (coApplicantMatch) {
    return `co_applicant.${coApplicantMatch[1]}.email` as FieldPath<InviteeFormSchemaType>;
  }

  return null;
}

function getApiEmailErrorPaths(error: unknown): FieldPath<InviteeFormSchemaType>[] {
  const sources = [
    (error as { errors?: unknown })?.errors,
    (error as { data?: unknown })?.data,
    (error as { response?: { data?: unknown } })?.response?.data,
  ];
  const paths: FieldPath<InviteeFormSchemaType>[] = [];

  sources.forEach((source) => {
    if (!source || typeof source !== "object" || Array.isArray(source)) return;

    Object.entries(source as Record<string, unknown>).forEach(([key, value]) => {
      const messages = Array.isArray(value) ? value : [value];
      const hasEmailMessage = messages.some(
        (message) =>
          typeof message === "string" && isEmailValidationMessage(message),
      );
      if (!key.toLowerCase().includes("email") && !hasEmailMessage) return;

      const path = normalizeInviteeEmailErrorPath(key);
      if (path && !paths.includes(path)) {
        paths.push(path);
      }
    });
  });

  return paths;
}

function mergeCoApplicantErrorArrays(
  ...sources: Array<FieldErrors["co_applicant"] | undefined>
): FieldErrors["co_applicant"] | undefined {
  const merged: Array<Record<string, unknown>> = [];

  sources.forEach((source) => {
    if (!Array.isArray(source)) return;

    source.forEach((item, index) => {
      if (!item || typeof item !== "object") return;
      merged[index] = {
        ...(merged[index] || {}),
        ...(item as Record<string, unknown>),
      };
    });
  });

  return merged.length > 0 ? (merged as unknown as FieldErrors["co_applicant"]) : undefined;
}

/** Get first error message from nested form errors (Angular: show first error in notify) */
function getFirstErrorMessage(errors: FieldErrors): string {
  if (!errors || typeof errors !== "object") return "Please correct the required fields.";
  const v = Object.values(errors)[0];
  if (!v) return "Please correct the required fields.";
  if (v && typeof v === "object" && "message" in v && typeof (v as any).message === "string")
    return (v as any).message;
  if (Array.isArray(v)) {
    for (let i = 0; i < v.length; i++) {
      const msg = getFirstErrorMessage((v[i] as any) || {});
      if (msg) return msg;
    }
  } else if (v && typeof v === "object")
    return getFirstErrorMessage(v as FieldErrors);
  return "Please correct the required fields.";
}

/** Get first error field name/path for scroll (e.g. co_applicant.0.first_name) */
function getFirstErrorFieldName(errors: FieldErrors, prefix = ""): string | null {
  if (!errors || typeof errors !== "object") return null;
  const entries = Object.entries(errors);
  for (const [key, value] of entries) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === "object" && "message" in value && typeof (value as any).message === "string")
      return path;
    if (Array.isArray(value)) {
      for (let i = 0; i < value.length; i++) {
        const found = getFirstErrorFieldName((value[i] as any) || {}, `${path}.${i}`);
        if (found) return found;
      }
    } else if (value && typeof value === "object") {
      const found = getFirstErrorFieldName(value as FieldErrors, path);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Ensure all co-applicant field errors are set (zodResolver can under-report array element errors).
 * Validates each co-applicant with coApplicantSchema, calls setError for every issue, and returns
 * merged co_applicant errors so the modal/scroll use the full list.
 */
function setCoApplicantErrors(
  getValues: () => InviteeFormSchemaType,
  setError: UseFormSetError<InviteeFormSchemaType>,
): FieldErrors["co_applicant"] {
  const values = getValues();
  const list = values?.co_applicant;
  const merged: Array<Record<string, { message: string }>> = [];
  if (!list || !Array.isArray(list)) return merged as unknown as FieldErrors["co_applicant"];
  for (let i = 0; i < list.length; i++) {
    const result = coApplicantSchema.safeParse(list[i]);
    const itemErrors: Record<string, { message: string }> = {};
    if (!result.success) {
      const err = result.error;
      const issues = "issues" in err ? (err as { issues: { path: (string | number)[]; message: string }[] }).issues : [];
      const seenPaths = new Set<string>();
      for (const issue of issues) {
        const path = issue.path && issue.path.length > 0 ? issue.path.join(".") : "root";
        if (path === "root" || seenPaths.has(path)) continue;
        seenPaths.add(path);
        const fieldPath = `co_applicant.${i}.${path}` as FieldPath<InviteeFormSchemaType>;
        setError(fieldPath, { type: "manual", message: issue.message });
        itemErrors[path] = { message: issue.message };
      }
    }
    merged[i] = itemErrors;
  }
  return merged as unknown as FieldErrors["co_applicant"];
}

export function InviteeAdd() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const { isLibertyRole } = useUserRole();
  const [loadingAction, setLoadingAction] = useState<"send" | "apply" | null>(
    null,
  );
  const [showBlockingModal, setShowBlockingModal] = useState(false);
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [inviteDetails, setInviteDetails] =
    useState<CreateInviteeResponse | null>(null);
  const [autoResubmit, setAutoResubmit] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  /** Set before blur when user interacts with Send Invitation / Apply Now so 500–2500 rent confirm does not open. */
  const skipRentConfirmationOnNextBlurRef = useRef(false);
  /** After min-rent validation modal closes, suppress rent confirmation blurs briefly (avoids chaining pop-ups). */
  const rentConfirmationSuppressUntilRef = useRef(0);
  const isCheckingDuplicate = useRef(false);
  const lastCheckedKey = useRef("");

  const popid = searchParams.get("popid");

  // In-app popups (no native "localhost says" dialogs)
  const [inviteExistsPopup, setInviteExistsPopup] = useState<{
    open: boolean;
    inviteCode: string | null;
    inviteData: Invitee | null;
    primaryInviteData: Invitee | null;
  }>({ open: false, inviteCode: null, inviteData: null, primaryInviteData: null });
  // Edit dialog when user clicks Yes on "Invitation exists" (Angular: dialogService.showdialog(ReportInviteEditComponent, ...))
  const [editExistsPopup, setEditExistsPopup] = useState<{
    open: boolean;
    invite: Invitee | null;
    parentInvite: Invitee | null;
  }>({ open: false, invite: null, parentInvite: null });
  const [matchingProfilePopup, setMatchingProfilePopup] = useState<{
    open: boolean;
    result: InviteeLookupResult | null;
    formPath: string | undefined;
    email: string;
    mobile: string;
  }>({ open: false, result: null, formPath: undefined, email: "", mobile: "" });
  const [autoResubmitPopup, setAutoResubmitPopup] = useState<{
    open: boolean;
    message: string;
    formData: InviteeFormSchemaType | null;
  }>({ open: false, message: "", formData: null });
  // Validation Message modal (Angular: same as 2nd screenshot – title "Validation Message", first error, Ok button)
  const [validationMessageModal, setValidationMessageModal] = useState<{
    open: boolean;
    message: string;
  }>({ open: false, message: "" });
  // Lead time validation: show center popup only (never notification), same as Angular when date is entered
  const [showLeadTimeValidationModal, setShowLeadTimeValidationModal] = useState(false);

  const form = useForm<InviteeFormSchemaType>({
    resolver: zodResolver(inviteeFormSchema),
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: {
      crime: undefined,
      approved_by_liberty: undefined,
      allow_to_movein: undefined,
      first_name: "",
      last_name: "",
      email: "",
      mobile_no: "",
      property_id: "",
      unit_number: "",
      rent_amount: "",
      lease_start_date: "",
      lease_duration: "12",
      income_verified: undefined,
      income: "",
      income_period: "MONTHLY",
      co_applicant: [],
    },
  });

  const { handleSubmit, watch, setValue, getValues, setError } = form;

  /**
   * Clicking Apply/Send moves focus off rent and can fire blur before submit handlers run.
   * That blur must not open the "confirm unusual rent" dialog when we are about to show (or are showing)
   * submit validation — especially for rent ≤ $10 (same click shows the min-rent modal).
   */
  const suppressRentBlurForSubmitInteraction = () => {
    rentConfirmationSuppressUntilRef.current = Date.now() + 1500;
  };

  /** Blur would open unusual-rent confirm for any numeric amount outside $500–$2500 — suppress during submit click. */
  const suppressUnusualRentBlurIfAmountOutsideStandardRange = () => {
    const raw = getValues("rent_amount");
    const n = parseRentAmountNumeric(raw ?? "");
    if (!Number.isNaN(n) && (n < 500 || n > 2500)) {
      suppressRentBlurForSubmitInteraction();
    }
  };

  const onInviteeValidationError = (errors: FieldErrors) => {
    const coAppErrors = setCoApplicantErrors(getValues, setError);
    const invalidEmailErrors = setInvalidInviteEmailFieldErrors(getValues(), setError);
    const mergedCoApplicantErrors = mergeCoApplicantErrorArrays(
      errors.co_applicant,
      coAppErrors,
      invalidEmailErrors.co_applicant,
    );
    const mergedErrors = {
      ...errors,
      ...invalidEmailErrors,
      ...(mergedCoApplicantErrors ? { co_applicant: mergedCoApplicantErrors } : {}),
    } as FieldErrors;
    const ordered = getFirstInviteeSubmitError(mergedErrors);
    const firstMessage =
      getInvalidEmailSummary(getValues()) ??
      ordered?.message ?? getFirstErrorMessage(mergedErrors);
    setValidationMessageModal({ open: true, message: firstMessage });
    setTimeout(
      () => focusFirstError(mergedErrors, ordered?.fieldPath ?? null),
      150,
    );
  };

  const handleInviteeFormValid = (
    data: InviteeFormSchemaType,
    action: "send" | "apply",
  ) => {
    const rentMsg = validateRentAmountForSubmit(data.rent_amount);
    if (rentMsg) {
      suppressRentBlurForSubmitInteraction();
      setError("rent_amount", { type: "manual", message: rentMsg });
      // Required / invalid: inline only (like emphasizing the field first). Min $10: center modal + inline.
      if (rentMsg === RENT_AMOUNT_MIN_SUBMIT_MESSAGE) {
        setValidationMessageModal({ open: true, message: rentMsg });
      }
      setTimeout(
        () =>
          focusFirstError(
            {
              rent_amount: { message: rentMsg, type: "manual" },
            } as FieldErrors,
            "rent_amount",
          ),
        150,
      );
      return;
    }
    void onSubmit(data, action);
  };

  // Handle popid query parameter
  useEffect(() => {
    if (popid) {
      addInviteLog(popid);
      // Remove popid from URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("popid");
      navigate(`/invite/add?${newSearchParams.toString()}`, {
        replace: true,
      });
    }
  }, [popid]);

  // Check for blocking conditions
  const checkBlockingConditions = (): boolean => {
    const crime = watch("crime");
    const approved_by_liberty = watch("approved_by_liberty");
    const allow_to_movein = watch("allow_to_movein");

    return (
      approved_by_liberty === "0" || crime === "1" || allow_to_movein === "1"
    );
  };

  // Duplicate detection - matches Angular checkForInviteEmail behavior
  // Only sends email and mobile_no params (not first_name/last_name)
  const checkForInviteEmail = async (formPath?: string) => {
    // Prevent re-entrant calls (blur events from setValue can cause loops)
    if (isCheckingDuplicate.current) return;

    const email = formPath
      ? watch((formPath + ".email") as any)
      : watch("email");
    const mobile = formPath
      ? watch((formPath + ".mobile_no") as any)
      : watch("mobile_no");

    // Only proceed if we have email or mobile (like Angular)
    if (!email && !mobile) return;

    // Skip if we already checked these exact values
    const checkKey = `${formPath || ""}|${email || ""}|${mobile || ""}`;
    if (lastCheckedKey.current === checkKey) return;

    isCheckingDuplicate.current = true;
    lastCheckedKey.current = checkKey;

    try {
      const result = await lookupInvitee({
        email: email || undefined,
        mobile_no: mobile || undefined,
        searchByEmail: true, // Only send email and mobile_no params
      });

      if (result && result.first_name) {
        setMatchingProfilePopup({
          open: true,
          result,
          formPath,
          email: email || "",
          mobile: mobile || "",
        });
      }
    } catch (error) {
      // Silently fail - duplicate detection is optional
      console.error("Duplicate detection failed:", error);
    } finally {
      isCheckingDuplicate.current = false;
    }
  };

  /** Focusing rent after Apply/Send triggers blur/focus chains and stacked rent modals — scroll only. */
  const isRentAmountFieldPath = (fieldPath: string) =>
    fieldPath === "rent_amount" || fieldPath.endsWith(".rent_amount");

  // Scroll to first invalid field (Angular: fromControls.focus(); fromControls.scrollIntoView())
  const focusFirstError = (
    errors?: FieldErrors,
    preferredFieldPath?: string | null,
  ) => {
    if (errors) {
      const fieldName = preferredFieldPath || getFirstErrorFieldName(errors);
      if (!fieldName) return;
      const nameWithUnderscores = fieldName.replace(/\./g, "_");
      const leafId = fieldName.split(".").pop() ?? fieldName;
      const element =
        document.querySelector(`[name="${fieldName}"]`) ??
        document.querySelector(`[name="${nameWithUnderscores}"]`) ??
        document.querySelector(`#${nameWithUnderscores}`) ??
        document.querySelector(`#${leafId}`) ??
        document.querySelector(`[id*="${leafId}"]`);
      if (element) {
        if (isRentAmountFieldPath(fieldName)) {
          (element as HTMLElement).scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          return;
        }
        (element as HTMLElement).focus?.();
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }
    const firstInvalidElement = document.querySelector(
      '.ng-invalid:not(form):not(div), [aria-invalid="true"], .border-red-500',
    ) as HTMLElement | null;
    if (firstInvalidElement) {
      const isRentInput =
        firstInvalidElement.id === "rent_amount" ||
        firstInvalidElement.getAttribute("data-testid") ===
          "invitee-add-primary-rent-input";
      if (isRentInput) {
        firstInvalidElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        return;
      }
      firstInvalidElement.focus();
      firstInvalidElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  // Handle form submission
  const onSubmit = async (
    data: InviteeFormSchemaType,
    action: "send" | "apply",
  ) => {
    // Prevent double submission
    if (loadingAction) return;

    // Check blocking conditions
    if (checkBlockingConditions()) {
      setShowBlockingModal(true);
      return;
    }

    setLoadingAction(action);

    try {
      // Format data for API - trim all strings and remove empty co_applicant like Angular
      const formattedData = validateData({
        ...data,
        popid: popid || undefined,
      } as any);

      // Remove empty co_applicant array if empty (like Angular)
      if (!(formattedData.co_applicant && formattedData.co_applicant.length)) {
        delete formattedData.co_applicant;
      }

      // Create invite
      const response = await createInvitee(formattedData);
      trackApplicationEvent('invite_created', 'resident_record');

      // Handle special cases (like Angular lines 301-303)
      if (response.formos_user || response.formos_co_user) {
        showWarning(
          "We found an existing profile with this email address. We have sent a forgot password email to this user along with their invite.",
        );
      }

      // Handle already_has_invite – Angular: editInvite(response.invite_data, response.primary_invite_data)
      if (response.already_has_invite) {
        setInviteExistsPopup({
          open: true,
          inviteCode: response.invite_code ?? null,
          inviteData: response.invite_data ?? null,
          primaryInviteData: response.primary_invite_data ?? null,
        });
        setLoadingAction(null);
        return;
      }

      // Handle action
      if (action === "send") {
        // Send invitation first, then set state to show success view
        try {
          await sendInvitation(response.invite_code);
          // Show success notification (like Angular line 423: "Invitation Send Successfully!")
          showSuccess("Invitation Send Successfully!");
          // Set state after sending to show success view
          setInviteCode(response.invite_code);
          setInviteDetails(response);
        } catch (sendError: any) {
          // Handle send invitation error (like Angular line 427)
          showError(formatApiError(sendError), false);
          return;
        }
      } else if (action === "apply") {
        // Angular submitForm('apply'): notify → 1s delay → moreInvite → logout → window.open('/apply/'+code)
        showSuccess("Invitation Data Added Successfully!");
        const applyUrl = `/apply/${response.invite_code}`;

        setTimeout(() => {
          // Reset form (Angular moreInvite)
          form.reset({
            crime: undefined,
            approved_by_liberty: undefined,
            allow_to_movein: undefined,
            first_name: "",
            last_name: "",
            email: "",
            mobile_no: "",
            property_id: "",
            unit_number: "",
            rent_amount: "",
            lease_start_date: "",
            lease_duration: "12",
            income_verified: undefined,
            income: "",
            income_period: "MONTHLY",
            co_applicant: [],
          });
          setInviteCode(null);
          setInviteDetails(null);
          setAutoResubmit(false);

          // Open apply in new tab first (Angular: window.open(..., '_blank'))
          window.open(applyUrl, "_blank");

          // Current tab: logout → login (Angular: router.navigate(['/auth/logout']))
          void logoutApi().finally(() => {
            dispatch(logout());
            window.location.href = "/login";
          });
        }, 1000);
        return;
      } else {
        // For other actions, set state normally
        setInviteCode(response.invite_code);
        setInviteDetails(response);
      }
    } catch (error: any) {
      const errorMessage = error.message || formatApiError(error);
      if (
        typeof errorMessage === "string" &&
        errorMessage.includes("already been invited and denied")
      ) {
        setAutoResubmitPopup({ open: true, message: errorMessage, formData: data });
        setLoadingAction(null);
        return;
      } else {
        const formattedError = formatApiError(error);
        const plainMessage = formattedError.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

        if (isLeadTimeValidationError(plainMessage)) {
          setShowLeadTimeValidationModal(true);
        } else if (
          (error?.status === 400 || error?.status === 422) &&
          plainMessage
        ) {
          const emailMessage = isEmailValidationMessage(plainMessage)
            ? plainMessage
            : "";
          const apiEmailPaths = emailMessage ? getApiEmailErrorPaths(error) : [];
          apiEmailPaths.forEach((path) => {
            setError(path, { type: "server", message: emailMessage });
          });
          const invalidEmailErrors = emailMessage
            ? setInvalidInviteEmailFieldErrors(getValues(), setError, emailMessage)
            : undefined;

          setValidationMessageModal({
            open: true,
            message:
              emailMessage || getInvalidEmailSummary(getValues()) || plainMessage,
          });
          if (apiEmailPaths.length > 0 || invalidEmailErrors) {
            setTimeout(
              () =>
                focusFirstError(
                  invalidEmailErrors,
                  apiEmailPaths[0] ?? null,
                ),
              150,
            );
          } else {
            focusFirstError();
          }
        } else {
          showError(formattedError, false);
          focusFirstError();
        }
      }
    } finally {
      setLoadingAction(null);
    }
  };

  // Apply matching profile when user confirms in popup
  const applyMatchingProfile = () => {
    const { result, formPath, email, mobile } = matchingProfilePopup;
    if (!result) return;
    if (formPath) {
      if (result.first_name) setValue((formPath + ".first_name") as any, result.first_name, { shouldValidate: true });
      if (result.last_name) setValue((formPath + ".last_name") as any, result.last_name, { shouldValidate: true });
      if (result.email) setValue((formPath + ".email") as any, result.email || "", { shouldValidate: true });
      if (result.mobile_no) setValue((formPath + ".mobile_no") as any, result.mobile_no || "", { shouldValidate: true });
      if (result.ssn) setValue((formPath + ".ssn_digit") as any, result.ssn, { shouldValidate: true });
      if (result.birthdate) setValue((formPath + ".birthdate") as any, result.birthdate, { shouldValidate: true });
      lastCheckedKey.current = `${formPath}|${result.email || email}|${result.mobile_no || mobile}`;
    } else {
      if (result.first_name) setValue("first_name", result.first_name, { shouldValidate: true });
      if (result.last_name) setValue("last_name", result.last_name, { shouldValidate: true });
      if (result.email) setValue("email", result.email || "", { shouldValidate: true });
      if (result.mobile_no) setValue("mobile_no", result.mobile_no || "", { shouldValidate: true });
      lastCheckedKey.current = `|${result.email || email}|${result.mobile_no || mobile}`;
    }
    setMatchingProfilePopup({ open: false, result: null, formPath: undefined, email: "", mobile: "" });
  };

  // Success view
  if (inviteCode && inviteDetails) {
    return (
      <InviteeSuccessView
        inviteCode={inviteCode}
        inviteDetails={inviteDetails}
        firstName={watch("first_name")}
        lastName={watch("last_name")}
        email={watch("email")}
        mobileNo={watch("mobile_no")}
        onStartNew={() => {
          // Reset form like Angular's moreInvite()
          form.reset({
            crime: undefined,
            approved_by_liberty: undefined,
            allow_to_movein: undefined,
            first_name: "",
            last_name: "",
            email: "",
            mobile_no: "",
            property_id: "",
            unit_number: "",
            rent_amount: "",
            lease_start_date: "",
            lease_duration: "12",
            income_verified: undefined,
            income: "",
            income_period: "MONTHLY",
            co_applicant: [],
          });
          setInviteCode(null);
          setInviteDetails(null);
          setAutoResubmit(false);
          // Reset validation state
          form.clearErrors();
        }}
        autoResubmit={autoResubmit}
      />
    );
  }

  return (
    <div data-testid="invitee-add-page" className={styles.pageContainer}>
      {/* Shared Header Component */}
      <PageHeader title="INVITE" />

      <div data-testid="invitee-add-content" className={styles.contentWrap}>
        <div className={styles.heroCard}>
          <h1
            data-testid="invitee-add-page-title"
            className={styles.pageTitle}
          >
            Add Invitation for Resident Application
          </h1>
        </div>

        <form
          ref={formRef}
          data-testid="invitee-add-form"
          noValidate
          onSubmit={handleSubmit(
            (data) => handleInviteeFormValid(data, "send"),
            onInviteeValidationError,
          )}
          className="form_inline"
        >
          <section className={styles.formSection}>
            <PropertyConfirmations form={form} />
          </section>

          <section className={styles.formSection}>
            <PrimaryApplicant
              form={form}
              skipRentConfirmationOnNextBlurRef={skipRentConfirmationOnNextBlurRef}
              rentConfirmationSuppressUntilRef={rentConfirmationSuppressUntilRef}
              onCheckDuplicate={() => checkForInviteEmail()}
            />
          </section>

          <section className={styles.formSection}>
            <CoApplicantSection
              form={form}
              onCheckDuplicate={(index) =>
                checkForInviteEmail(`co_applicant.${index}`)
              }
            />
          </section>

          {/* Action Buttons */}
          <div
            data-testid="invitee-add-form-actions"
            className={styles.actionButtons}
            onPointerDownCapture={(e) => {
              const t = e.target as HTMLElement | null;
              if (
                t?.closest?.(
                  '[data-testid="invitee-add-send-invitation-button"], [data-testid="invitee-add-apply-now-button"]',
                )
              ) {
                suppressUnusualRentBlurIfAmountOutsideStandardRange();
              }
              const rent = document.getElementById("rent_amount");
              if (rent && document.activeElement === rent) {
                skipRentConfirmationOnNextBlurRef.current = true;
              }
            }}
            onKeyDownCapture={(e) => {
              if (e.key !== "Enter" && e.key !== " ") return;
              const t = e.target as HTMLElement | null;
              if (
                t?.closest?.(
                  '[data-testid="invitee-add-send-invitation-button"], [data-testid="invitee-add-apply-now-button"]',
                )
              ) {
                suppressUnusualRentBlurIfAmountOutsideStandardRange();
              }
            }}
          >
            <button
              type="submit"
              data-testid="invitee-add-send-invitation-button"
              disabled={loadingAction === "send"}
              className={styles.sendButton}
            >
              {loadingAction === "send" && (
                <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
              )}
              Send Invitation
            </button>
            <button
              type="button"
              data-testid="invitee-add-apply-now-button"
              onClick={handleSubmit(
                (data) => handleInviteeFormValid(data, "apply"),
                onInviteeValidationError,
              )}
              disabled={loadingAction === "apply"}
              className={styles.applyButton}
            >
              {loadingAction === "apply" && (
                <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
              )}
              Apply Now
            </button>
          </div>
        </form>

        {/* Blocking Conditions Modal */}
        <BlockingConditionsModal
          isOpen={showBlockingModal}
          onClose={() => setShowBlockingModal(false)}
          overlayDataTestId="invitee-add-blocking-conditions-modal"
          closeButtonDataTestId="invitee-add-blocking-conditions-modal-close-button"
          okButtonDataTestId="invitee-add-blocking-conditions-modal-ok-button"
        />

        {/* Matching profile – structured dialog with formatted SSN (XXX-XX-8759) and Mobile ((987) 645-2622) */}
        <InviteeLookupDialog
          isOpen={matchingProfilePopup.open}
          lookupResult={matchingProfilePopup.result}
          onConfirm={applyMatchingProfile}
          onCancel={() =>
            setMatchingProfilePopup({
              open: false,
              result: null,
              formPath: undefined,
              email: "",
              mobile: "",
            })
          }
          showSSN={isLibertyRole}
          showDOB={isLibertyRole}
        />

        {/* Invitation Exists – Angular: editInvite() (invite-add.component.ts ~343–361). InviteModule: path '' = list, 'add' = form.
            - Yes → edit dialog; on save success navigate to ['/invite'] = invite list (no query)
            - No → list with invite_code query
            - X on first modal → close only
        */}
        {inviteExistsPopup.open && !editExistsPopup.open && (
          <ConfirmDialog
            isOpen={inviteExistsPopup.open}
            title="Invitation Exists for Applicant"
            message="Do you want to correct the email address or phone #?"
            primaryLabel="Yes"
            secondaryLabel="No"
            primaryFirst
            presentation="saveChanges"
            overlayDataTestId="invitee-add-invitation-exists-modal"
            primaryButtonDataTestId="invitee-add-invitation-exists-yes-button"
            secondaryButtonDataTestId="invitee-add-invitation-exists-no-button"
            closeButtonDataTestId="invitee-add-invitation-exists-modal-close-button"
            onDismiss={() => {
              setInviteExistsPopup({ open: false, inviteCode: null, inviteData: null, primaryInviteData: null });
            }}
            onPrimary={() => {
              const { inviteData, primaryInviteData, inviteCode } = inviteExistsPopup;
              setInviteExistsPopup({ open: false, inviteCode: null, inviteData: null, primaryInviteData: null });
              setEditExistsPopup({
                open: true,
                invite: inviteData ?? ({ invite_code: inviteCode } as any),
                parentInvite: primaryInviteData ?? null,
              });
            }}
            onSecondary={() => {
              const code = inviteExistsPopup.inviteCode;
              setInviteExistsPopup({ open: false, inviteCode: null, inviteData: null, primaryInviteData: null });
              navigate(code ? `/invite?invite_code=${code}` : "/invite");
            }}
          />
        )}

        {/* Edit Invite dialog (Angular: ReportInviteEditComponent with title 'Edit Invite - Applicant Info') */}
        <EditInviteeDialog
          isOpen={editExistsPopup.open}
          invite={editExistsPopup.invite}
          parentInvite={editExistsPopup.parentInvite}
          type="edit"
          onClose={() => setEditExistsPopup({ open: false, invite: null, parentInvite: null })}
          onSuccess={() => {
            setEditExistsPopup({ open: false, invite: null, parentInvite: null });
            // Angular: childEvent type 'refresh' → router.navigate(['/invite']) = ReportsInvitesComponent (no queryParams)
            navigate("/invite", { replace: true });
            showSuccess("Invitation updated successfully.");
          }}
        />

        {/* Validation review – application-module style. */}
        <ConfirmDialog
          isOpen={validationMessageModal.open}
          title="Validation"
          message={validationMessageModal.message}
          primaryLabel="Continue"
          secondaryLabel="Cancel"
          presentation="validationReview"
          hideValidationReviewIntro
          overlayDataTestId="invitee-add-validation-message-confirm-dialog"
          primaryButtonDataTestId="invitee-add-validation-message-continue-button"
          secondaryButtonDataTestId="invitee-add-validation-message-cancel-button"
          closeButtonDataTestId="invitee-add-validation-message-close-button"
          onPrimary={() => {
            if (validationMessageModal.message === RENT_AMOUNT_MIN_SUBMIT_MESSAGE) {
              rentConfirmationSuppressUntilRef.current = Date.now() + 600;
            }
            setValidationMessageModal({ open: false, message: "" });
          }}
          onSecondary={() => {
            if (validationMessageModal.message === RENT_AMOUNT_MIN_SUBMIT_MESSAGE) {
              rentConfirmationSuppressUntilRef.current = Date.now() + 600;
            }
            setValidationMessageModal({ open: false, message: "" });
          }}
          onDismiss={() => {
            if (validationMessageModal.message === RENT_AMOUNT_MIN_SUBMIT_MESSAGE) {
              rentConfirmationSuppressUntilRef.current = Date.now() + 600;
            }
            setValidationMessageModal({ open: false, message: "" });
          }}
        />

        {/* Lead time: center popup only (never notification), matches Angular dialogService.confirm */}
        <InviteeValidationMessageModal
          isOpen={showLeadTimeValidationModal}
          message={LEASE_LEAD_TIME_VALIDATION_HTML}
          onClose={() => setShowLeadTimeValidationModal(false)}
          overlayDataTestId="invitee-add-lead-time-validation-modal"
          closeButtonDataTestId="invitee-add-lead-time-validation-modal-close-button"
          okButtonDataTestId="invitee-add-lead-time-validation-modal-ok-button"
        />

        {/* Auto-resubmit – in-app popup */}
        <ConfirmDialog
          isOpen={autoResubmitPopup.open}
          title="Auto-resubmit invite"
          message={`${autoResubmitPopup.message}\n\nWould you like to Auto-Resubmit?`}
          primaryLabel="Yes"
          primaryFirst
          presentation="saveChanges"
          overlayDataTestId="invitee-add-auto-resubmit-confirm-dialog"
          primaryButtonDataTestId="invitee-add-auto-resubmit-yes-button"
          secondaryButtonDataTestId="invitee-add-auto-resubmit-no-button"
          closeButtonDataTestId="invitee-add-auto-resubmit-close-button"
          onPrimary={async () => {
            const data = autoResubmitPopup.formData;
            setAutoResubmitPopup({ open: false, message: "", formData: null });
            if (!data) return;
            try {
              const formattedData = validateData({
                ...data,
                resubmit_invite: "1",
                popid: popid || undefined,
              } as any);
              if (!(formattedData.co_applicant && formattedData.co_applicant.length)) {
                delete formattedData.co_applicant;
              }
              const response = await createInvitee(formattedData);
              trackApplicationEvent('invite_created', 'resident_record');
              showSuccess("Invite Data Added Successfully!");
              setInviteCode(response.invite_code);
              setInviteDetails(response);
              setAutoResubmit(true);
            } catch (resubmitError: any) {
              const formatted = formatApiError(resubmitError);
              const plain = formatted.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
              const st = resubmitError?.status as number | undefined;
              if ((st === 400 || st === 422) && plain) {
                setValidationMessageModal({ open: true, message: plain });
              } else {
                showError(formatted, false);
              }
            }
          }}
          secondaryLabel="No"
          onSecondary={() =>
            setAutoResubmitPopup({ open: false, message: "", formData: null })
          }
          variant="warning"
        />
      </div>
    </div>
  );
}
